<script setup lang="ts">
import { generateTranslationKey } from '~/utilities/data-transformers';
import FloatLabel from 'primevue/floatlabel';
import TextArea from 'primevue/textarea';
import type { IElement } from '~/interfaces/form/formBuilder';

const props = defineProps<{
  element: IElement;
  formErrors: { [key: string]: string };
}>();
const inputRef = ref<typeof TextArea>();
const model = defineModel<string>();
const emits = defineEmits<{
  (e: 'inputBlur'): void;
  (e: 'inputChange', id: string): void;
  (e: 'update:modelValue', value: string): void;
}>();
const site = useSiteStore();

const internalModel = ref('');

const getNativeTextarea = () => inputRef.value?.$el;

const resizeTextarea = () => {
  const textarea = getNativeTextarea();
  if (!textarea) return;

  textarea.style.height = 'auto';
  textarea.style.height = `${textarea.scrollHeight}px`;
};

const handleValueChange = (event: Event) => {
  const target = event.target as HTMLTextAreaElement;
  internalModel.value = target.value.trim();
  emits('update:modelValue', target.value);
  emits('inputChange', props.element.id);
  nextTick(resizeTextarea);
};
const touched = ref<boolean>(false);

const contentLength = computed(() => {
  const hasMinMax = props.element.regex.match(/\{(\d*),?(\d*)\}/);
  if (hasMinMax) {
    const minLength = hasMinMax[1] ? parseInt(hasMinMax[1]) : 0;
    const maxLength = hasMinMax[2] ? parseInt(hasMinMax[2]) : 0;
    return { minLength, maxLength };
  }

  return {
    minLength: 0,
    maxLength: 512,
  };
});
onMounted(() => {
  internalModel.value = model.value;
  nextTick(resizeTextarea);
});
watch(
  () => model.value,
  () => {
    internalModel.value = model.value;
    nextTick(resizeTextarea);
  },
);
</script>
<template>
  <div>
    <FloatLabel
      v-if="!!element"
      :id="`${element.id.replace(' ', '')}-label`"
      :input-type="element.inputType"
      :disabled="element.disabled"
      :touched="touched || !!internalModel"
      variant="in"
      :data-prefix="!!element?.prefix"
    >
      <TextArea
        :id="element.id"
        ref="inputRef"
        :value="internalModel"
        autocomplete="off"
        autocorrect="off"
        autocapitalize="off"
        :auto-resize="false"
        spellcheck="false"
        :aria-label="element.label"
        :required="element.required"
        :disabled="element.disabled"
        :invalid="!!formErrors[element.id]"
        :name="element.id"
        :touched="touched || !!internalModel"
        :placeholder="
          touched
            ? $t(
                `placeholder.${generateTranslationKey(element.placeholder)}-${site.getRegionCode}`,
                `placeholder.${generateTranslationKey(element.placeholder)}`,
              )
            : ''
        "
        v-bind="contentLength"
        @input="handleValueChange($event)"
        @click="touched = true"
        @focus="touched = true"
        @change="handleValueChange($event)"
        @blur="((model = model?.trim()), emits('inputBlur'), (touched = false))"
      />

      <label :for="element.id">
        {{
          $t(
            `label.${generateTranslationKey(element.label)}-${site.getRegionCode}`,
            `label.${generateTranslationKey(element.label)}`,
          )
        }}
      </label>
    </FloatLabel>
    <span
      :class="[
        'text-xs text-base-color',
        {
          'text-error-500':
            !!internalModel && internalModel?.length < contentLength.minLength,
        },
      ]"
    >
      {{ internalModel?.length || 0 }}/{{ contentLength.maxLength }}
    </span>
  </div>
</template>
